import { supabase } from "@/integrations/supabase/client";

export const STORY_IMAGE_BUCKET = "story-images";
export const MAX_STORY_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB
export const ACCEPTED_STORY_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const STORY_IMAGE_ACCEPT_ATTR = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";

const EXT_BY_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

/** Magic-byte signatures so a renamed .exe can't slip through on MIME alone. */
const readSignature = async (file: File): Promise<string | null> => {
  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const hex = (n: number) => bytes[n]?.toString(16).padStart(2, "0") ?? "";
  if (hex(0) === "ff" && hex(1) === "d8" && hex(2) === "ff") return "image/jpeg";
  if (hex(0) === "89" && hex(1) === "50" && hex(2) === "4e" && hex(3) === "47") return "image/png";
  const riff = String.fromCharCode(...bytes.slice(0, 4));
  const webp = String.fromCharCode(...bytes.slice(8, 12));
  if (riff === "RIFF" && webp === "WEBP") return "image/webp";
  return null;
};

export type StoryImageValidation =
  | { ok: true; file: File; message?: undefined }
  | { ok: false; file?: undefined; message: string };

/** Client-side validation: type, size, and real image bytes. */
export const validateStoryImage = async (file: File): Promise<StoryImageValidation> => {
  if (!(ACCEPTED_STORY_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, message: "Please upload a JPG, PNG, or WebP image." };
  }
  if (file.size > MAX_STORY_IMAGE_BYTES) {
    return { ok: false, message: "That image is larger than 5 MB. Please upload a smaller version." };
  }
  if (file.size === 0) {
    return { ok: false, message: "That file appears to be empty." };
  }
  const signature = await readSignature(file);
  if (signature !== file.type) {
    return { ok: false, message: "That file doesn't look like a valid image." };
  }
  return { ok: true, file };
};

const randomName = (ext: string) => {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `stories/${id}.${ext}`;
};

/** Uploads to the private bucket and returns the object path to store on the row. */
export const uploadStoryImage = async (file: File): Promise<string> => {
  const path = randomName(EXT_BY_TYPE[file.type] ?? "jpg");
  const { error } = await supabase.storage.from(STORY_IMAGE_BUCKET).upload(path, file, {
    contentType: file.type,
    cacheControl: "3600",
    upsert: false,
  });
  if (error) throw error;
  return path;
};

/**
 * Signs a story image URL. Storage policies only allow reads for images whose
 * story is published (or for admins), so unapproved images resolve to null.
 */
export const signStoryImage = async (path: string, expiresIn = 3600): Promise<string | null> => {
  const { data, error } = await supabase.storage.from(STORY_IMAGE_BUCKET).createSignedUrl(path, expiresIn);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
};

export const signStoryImages = async (
  paths: string[],
  expiresIn = 3600,
): Promise<Record<string, string>> => {
  const unique = Array.from(new Set(paths.filter(Boolean)));
  const entries = await Promise.all(
    unique.map(async (p) => [p, await signStoryImage(p, expiresIn)] as const),
  );
  return Object.fromEntries(entries.filter(([, url]) => !!url) as [string, string][]);
};

/** Public story pages: signed by the server, and only for published stories. */
export const signPublishedStoryImages = async (paths: string[]): Promise<Record<string, string>> => {
  const unique = Array.from(new Set(paths.filter(Boolean)));
  if (!unique.length) return {};
  const { data, error } = await supabase.functions.invoke("story-image-urls", { body: { paths: unique } });
  if (error) return {};
  return (data as { urls?: Record<string, string> })?.urls ?? {};
};
